import { css, html } from 'lit';
import { property } from 'lit/decorators.js';
import { KryptonApp } from '../krypton-app';
import { waitFor } from '../modules/async';
import { ICONS } from '../modules/resources';
import { LayerElement } from './layer-element';
const ALLOW_MANUAL_CLOSE_AFTER: number = 60000;

class Message {
    value: string;
    duration: number;
    type: string;
    image: boolean;
    opacity: number;

    constructor(message: string, duration?: number, type?: string, image?: boolean, opacity?: number) {
        this.value = message;
        if (duration != undefined) this.duration = duration;
        if (type != undefined) this.type = type;
        if (image != undefined) this.image = image;
        if (opacity != undefined) this.opacity = opacity;
    }

    static fromJson(messageJson): Message {
        return new Message(
            messageJson.msg,
            messageJson.duration,
            messageJson.type,
            messageJson.image,
            messageJson.opacity
        );
    }

}

class MessageTicker {

    private messages: Message[];
    private currentIndex: number;
    private status: boolean;

    constructor(messages: Message[]) {
        this.messages = messages;
        this.currentIndex = 0;
    }

    async start(onTick?: Function) {
        if (!this.messages || !this.messages.length)
            return;
        this.status = true;
        for (let i = 0; i < this.messages.length; i++) {
            if (!this.status)
                return;
            this.currentIndex = i;
            onTick && onTick();
            await waitFor(this.current.duration);
        }
    }

    get current() {
        return this.messages[this.currentIndex];
    }

    stop() {
        this.status = false;
    }

    reset() {
        this.stop();
        this.currentIndex = 0;
    }

}

let MESSAGES: Map<string, Message[]> = new Map();
(async () => {
    let config = await fetch(KryptonApp.url.message).then(r => r.json());
    for (let [key, value] of Object.entries(config) as [any, Message[]]) {
        let messages: Message[] = [];
        value.forEach(m => messages.push(Message.fromJson(m)));
        MESSAGES.set(key, messages);
    }
})();

export class LoadingCurtain extends LayerElement {

    _opened: boolean = false;
    @property({ type: Number }) accessor allowManualCloseAfter: number = ALLOW_MANUAL_CLOSE_AFTER;
    @property({ type: Message, reflect: true, converter: {} }) accessor message: Message = new Message("loading");
    @property({ type: MessageTicker, reflect: true, converter: {} }) accessor messageTicker: MessageTicker;
    private showDelayTimerHandler: any;
    private showCloseMeTimerHandler: any;

    constructor() {
        super();
        this.animationStyle = "fade";
        this.backgroundStyle = "opaque-light";
    }

    connectedCallback() {
        this.addEventListener("message-ticker-update", this.onMessageTickerUpdate.bind(this) as EventListener);
        super.connectedCallback();
    }

    onMessageTickerUpdate(e: CustomEvent) {
        if (!e || !e.type) return;
        e.stopPropagation();
        e.detail.cssClass && this.classList.add(e.detail.cssClass);
    }

    reset() {
        this.allowManualCloseAfter = ALLOW_MANUAL_CLOSE_AFTER;
    }

    private _closeable: boolean = false;

    get closeable(): boolean {
        return this._closeable;
    }

    @property({ type: Boolean, reflect: true })
    set closeable(closeable: boolean) {
        const oldValue = this._closeable;
        this._closeable = closeable;
        this.requestUpdate("closeable", oldValue);
    }

    get open() {
        return super.open;
    }

    @property({ type: Boolean, reflect: true })
    set open(open: any) {

        this.closeable = false;

        let messages: Message[];

        if (typeof open == "string") {
            messages = (MESSAGES.has(open) ? MESSAGES.get(open) : [new Message(open)]) as Message[];
        }

        if (open) {
            this.showDelayTimerHandler = setTimeout(() => {
                this.showDelayTimerHandler = false;
                super.open = true;
                this.messageTicker = new MessageTicker(messages || MESSAGES.get("loading") as Message[]);
                this.messageTicker.start(this.updateMessage.bind(this));
                if (this.allowManualCloseAfter > 0 && !this.showCloseMeTimerHandler) {
                    this.showCloseMeTimerHandler = setTimeout(() => {
                        this.showCloseMeTimerHandler = false;
                        this.closeable = true;
                    }, this.allowManualCloseAfter);
                }
            }, 100); //-- slight delay for effect
        } else {
            if (this.showDelayTimerHandler) {
                clearTimeout(this.showDelayTimerHandler);
                this.showDelayTimerHandler = false;
            }
            if (this.showCloseMeTimerHandler) {
                clearTimeout(this.showCloseMeTimerHandler);
                this.showCloseMeTimerHandler = false;
            }
            super.open = false;
            this.closeable = false;
        }
    }

    get opened(): boolean {
        return this._opened;
    }

    @property({ type: Boolean, reflect: true })
    set opened(opened: boolean) {
        const oldValue = this._opened;
        this._opened = opened;
        opened ? this.animateShow(true) : this.animateHide(true);
        this.requestUpdate("opened", oldValue);
    }

    onClickClose(e: Event) {
        this.open = false;
    }

    updateMessage() {
        this.message = this.messageTicker.current;

        let messageElement = this.shadowRoot?.getElementById("message");
        messageElement?.classList.remove(...messageElement.classList);
        if (typeof this.message.type == "string") {
            messageElement?.classList.add(this.message.type);
            if (this.message.type == "simple") {
                this.closeable = true;
            }
        }

        this.style.removeProperty("background");
        if (typeof this.message.opacity == "number") {
            let backgroundColor = getComputedStyle(this)["background-color"];
            this.style.setProperty("background-color", backgroundColor.replace(/[0-9\.]+\)$/, this.message.opacity));
        }

        let imageElement = this.shadowRoot?.getElementById("image");
        imageElement?.style.removeProperty("visibility");
        if (typeof this.message.image == "boolean") imageElement?.style.setProperty("visibility", this.message.image ? "visible" : "hidden");
        this.requestUpdate("message");
    }


    static finalizeStyles(styles: any) {
        return [
            styles ? styles : css``,
            css`
            :host {
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                width: 100%;
                padding: 1.5rem;
                box-sizing: border-box;
                z-index: 10000;
                z-index: var(--z-index-loading-curtain);
                background-color: rgba(255, 255, 255, 0.85);
                -webkit-transition: opacity 0.5s 0s ease;
                -moz-transition: opacity 0.5s 0s ease;
                -o-transition: opacity 0.5s 0s ease;
                transition: opacity 0.5s 0s ease;
                flex-direction: column;
                align-items: center;
                align-content: space-between;
            }

            :host(.wake) {
                display: flex !important;
            }

            :host(.simple) #image {
                display: none;
            }

            #message {
                margin-top: 5%;
            }

            #image {
                margin: 1rem auto 2rem;
            }

            #close-button {
                background: transparent;
                border: none;
                cursor: pointer;
            }

            #close-button>span,
            #close-button>img {
                vertical-align: middle;
            }

            #close-button.hide {
                display: none;
            }

            #message {
                font-size: 3.5rem;
                line-height: 4rem;
                color: #004693;
                display: inline-block;
            }

            :host(.short) #message {
                font-size: 2rem;
                line-height: 1rem;
                color: #004693;
                display: inline-block;
                margin-top: -16px !important;
            }

            #message.simple {
                color: #999999;
                font-size: 2rem;
                line-height: 2.5rem;
            }

            .sk-cube-grid {
                width: 40px;
                height: 40px;
                margin: 100px auto;
              }

              :host(.short) .sk-cube-grid {
                width: 20px;
                height: 20px;
                margin: 2px auto;
              }

              .sk-cube-grid .sk-cube {
                width: 33%;
                height: 33%;
                min-width: 5px;
                min-height: 5px;
                background-color: #a8c6d0;
                float: left;
                -webkit-animation: sk-cubeGridScaleDelay 1.3s infinite ease-in-out;
                        animation: sk-cubeGridScaleDelay 1.3s infinite ease-in-out;
              }
              .sk-cube-grid .sk-cube1 {
                -webkit-animation-delay: 0.2s;
                        animation-delay: 0.2s; }
              .sk-cube-grid .sk-cube2 {
                -webkit-animation-delay: 0.3s;
                        animation-delay: 0.3s; }
              .sk-cube-grid .sk-cube3 {
                -webkit-animation-delay: 0.4s;
                        animation-delay: 0.4s; }
              .sk-cube-grid .sk-cube4 {
                -webkit-animation-delay: 0.1s;
                        animation-delay: 0.1s; }
              .sk-cube-grid .sk-cube5 {
                -webkit-animation-delay: 0.2s;
                        animation-delay: 0.2s; }
              .sk-cube-grid .sk-cube6 {
                -webkit-animation-delay: 0.3s;
                        animation-delay: 0.3s; }
              .sk-cube-grid .sk-cube7 {
                -webkit-animation-delay: 0s;
                        animation-delay: 0s; }
              .sk-cube-grid .sk-cube8 {
                -webkit-animation-delay: 0.1s;
                        animation-delay: 0.1s; }
              .sk-cube-grid .sk-cube9 {
                -webkit-animation-delay: 0.2s;
                        animation-delay: 0.2s; }

              @-webkit-keyframes sk-cubeGridScaleDelay {
                0%, 70%, 100% {
                  -webkit-transform: scale3D(1, 1, 1);
                          transform: scale3D(1, 1, 1);
                } 35% {
                  -webkit-transform: scale3D(0, 0, 1);
                          transform: scale3D(0, 0, 1);
                }
              }

              @keyframes sk-cubeGridScaleDelay {
                0%, 70%, 100% {
                  -webkit-transform: scale3D(1, 1, 1);
                          transform: scale3D(1, 1, 1);
                } 35% {
                  -webkit-transform: scale3D(0, 0, 1);
                          transform: scale3D(0, 0, 1);
                }
              }
        `];
    }

    render() {
        return html`
            <div id="message">${this.message.value}</div>
            <div id="image" class="sk-cube-grid">
                <div class="sk-cube sk-cube1"></div>
                <div class="sk-cube sk-cube2"></div>
                <div class="sk-cube sk-cube3"></div>
                <div class="sk-cube sk-cube4"></div>
                <div class="sk-cube sk-cube5"></div>
                <div class="sk-cube sk-cube6"></div>
                <div class="sk-cube sk-cube7"></div>
                <div class="sk-cube sk-cube8"></div>
                <div class="sk-cube sk-cube9"></div>
            </div>
            <button id="close-button" type="button" @click="${this.onClickClose}" class="${this.closeable ? "show" : "hide"}">
                <span>Close me</span>
                <img src="${ICONS.x.file}">
            </button>
        `;
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'loading-curtain': LoadingCurtain;
    }
}

